import {
  Close,
  CloseFullscreen,
  Fullscreen,
  Minimize,
  OpenInFull,
  OpenInNew as OpenInNewIcon,
  Refresh,
  Work as WorkIcon,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

interface WorkModalHeaderProps {
  workId: number | null;
  workName?: string;
  customerName?: string;
  customerId?: number;
  categoryName?: string;
  isRefreshing: boolean;
  minimized: boolean;
  fullscreen: boolean;
  onToggleMinimize: () => void;
  onToggleFullscreen: () => void;
  onClose: () => void;
}

const WorkModalHeader = ({
  workId,
  workName,
  customerName,
  customerId,
  categoryName,
  isRefreshing,
  minimized,
  fullscreen,
  onToggleMinimize,
  onToggleFullscreen,
  onClose,
}: WorkModalHeaderProps) => {
  const navigate = useNavigate();
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        px: 2,
        py: 1,
        bgcolor: "primary.main",
        color: "primary.contrastText",
        borderRadius: fullscreen ? 0 : "12px 12px 0 0",
        cursor: "pointer",
        userSelect: "none",
      }}
      onClick={minimized ? onToggleMinimize : undefined}
    >
      <Box display="flex" alignItems="center" gap={1.5} minWidth={0}>
        <Avatar
          sx={{
            bgcolor: "primary.dark",
            width: 32,
            height: 32,
          }}
        >
          <WorkIcon fontSize="small" />
        </Avatar>
        <Box minWidth={0}>
          <Box display="flex" alignItems="center" gap={0.5}>
            <Typography
              variant="subtitle2"
              fontWeight="600"
              noWrap
              sx={{ color: "inherit" }}
            >
              Ärende #{workId} {workName || ""}{customerName ? ` - ${customerName}` : ""}
            </Typography>
            {customerId && !minimized && (
              <Tooltip title="Gå till kundprofil">
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/customer/${customerId}`);
                  }}
                  size="small"
                  sx={{
                    color: "primary.contrastText",
                    opacity: 0.8,
                    "&:hover": { opacity: 1, bgcolor: "primary.dark" },
                    p: 0.25,
                  }}
                >
                  <OpenInNewIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          {!minimized && (
            <Typography variant="caption" sx={{ color: "primary.contrastText", opacity: 0.8 }}>
              {categoryName || "Kategori ej tillgänglig"}
            </Typography>
          )}
        </Box>
        {isRefreshing && (
          <CircularProgress size={14} thickness={4} sx={{ color: "primary.contrastText", ml: 1 }} />
        )}
        {isRefreshing && (
          <Refresh
            sx={{
              color: "primary.contrastText",
              fontSize: 14,
              animation: "spin 1s linear infinite",
              "@keyframes spin": {
                "0%": { transform: "rotate(0deg)" },
                "100%": { transform: "rotate(360deg)" },
              },
            }}
          />
        )}
      </Box>
      <Box display="flex" alignItems="center" gap={0.5}>
        {!minimized && (
          <Tooltip title={fullscreen ? "Återställ storlek" : "Helskärm"}>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                onToggleFullscreen();
              }}
              size="small"
              sx={{
                color: "primary.contrastText",
                "&:hover": { bgcolor: "primary.dark" },
              }}
            >
              {fullscreen ? <CloseFullscreen sx={{ fontSize: 16 }} /> : <Fullscreen sx={{ fontSize: 16 }} />}
            </IconButton>
          </Tooltip>
        )}
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            onToggleMinimize();
          }}
          size="small"
          sx={{
            color: "primary.contrastText",
            "&:hover": { bgcolor: "primary.dark" },
          }}
        >
          {minimized ? <OpenInFull sx={{ fontSize: 16 }} /> : <Minimize sx={{ fontSize: 16 }} />}
        </IconButton>
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          size="small"
          sx={{
            color: "primary.contrastText",
            "&:hover": { bgcolor: "error.main" },
          }}
        >
          <Close sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>
    </Box>
  );
};

export default WorkModalHeader;
